/**
 * Text measurement implementations.
 *
 * The canvas measurer is the real one. The estimating fallback exists because
 * jsdom has no 2D context, and because a headless environment must still be able
 * to lay out text rather than throw.
 */

import { fontShorthand, type FontSpec, type MeasureText, type TextMetrics } from '../geometry/drawable';

function cacheKey(text: string, font: FontSpec): string {
  return `${font.size}|${font.weight}|${font.style}|${font.family}|${text}`;
}

/** Rough metrics from character count. Used only when no 2D context exists. */
export function createEstimatingMeasurer(): MeasureText {
  return (text: string, font: FontSpec): TextMetrics => ({
    width: text.length * font.size * 0.55,
    ascent: font.size * 0.8,
    descent: font.size * 0.2,
  });
}

export function createCanvasMeasurer(): MeasureText {
  let ctx: CanvasRenderingContext2D | null = null;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    ctx = canvas.getContext('2d');
  } catch {
    ctx = null;
  }
  if (!ctx) return createEstimatingMeasurer();

  const context = ctx;
  const cache = new Map<string, TextMetrics>();

  return (text: string, font: FontSpec): TextMetrics => {
    const key = cacheKey(text, font);
    const hit = cache.get(key);
    if (hit) return hit;
    // The exact same shorthand the painter and the editing textarea use.
    context.font = fontShorthand(font);
    const m = context.measureText(text);
    const metrics: TextMetrics = {
      width: m.width,
      ascent: m.actualBoundingBoxAscent || font.size * 0.8,
      descent: m.actualBoundingBoxDescent || font.size * 0.2,
    };
    if (cache.size > 4000) cache.clear();
    cache.set(key, metrics);
    return metrics;
  };
}
